import { RouteOption } from '@/models/tutorial'
import { ROUTES, UTILS } from '@/shared/constants'

type TutorialRouteDefinition = [value: string, label: string]

const routeGroup = (
  module: string,
  definitions: TutorialRouteDefinition[]
): RouteOption[] =>
  definitions.map(([value, label]) => ({ value, label, module }))

export const tutorialRouteOptions: RouteOption[] = [
  ...routeGroup('Início', [[UTILS.SLASH, 'Dashboard']]),
  ...routeGroup('Atendimento', [
    ['/conversations', 'Atendimentos'],
    ['/conversations/view/:id', 'Visualizar atendimento'],
    ['/scheduled-messages', 'Agendamentos'],
    ['/assistant', 'Assistente'],
  ]),
  ...routeGroup('Clientes', [
    ['/clients', 'Clientes'],
    ['/clients/new', 'Criar cliente'],
    ['/clients/:id', 'Editar cliente'],
    ['/clients/import', 'Importar clientes'],
    [ROUTES.OPPORTUNITIES.PATH, ROUTES.OPPORTUNITIES.LABEL],
  ]),
  ...routeGroup('Jornadas', [
    ['/journeys', 'Jornadas'],
    ['/journeys/new', 'Criar jornada'],
    ['/journeys/:id', 'Editar jornada'],
    ['/journeys/view/:id', 'Visualizar jornada'],
    ['/journeys/view/client/:id', 'Jornada do cliente'],
    ['/journeys/view/templates', 'Jornadas padrão disponíveis'],
    ['/journeys/view/templates/:id', 'Visualizar jornada padrão'],
  ]),
  ...routeGroup('Ordens de serviço', [
    ['/service-orders', 'Ordens de serviço'],
    ['/service-orders/new', 'Criar ordem de serviço'],
    ['/service-orders/:id', 'Editar ordem de serviço'],
    ['/service-orders/view/:id', 'Visualizar ordem de serviço'],
  ]),
  ...routeGroup('Listas de transmissão', [
    ['/transmission-lists', 'Listas de transmissão'],
    ['/transmission-lists/new', 'Criar lista de transmissão'],
    ['/transmission-lists/:id', 'Editar lista de transmissão'],
    ['/transmission-lists/view/:id', 'Visualizar lista de transmissão'],
  ]),
  ...routeGroup('Operação', [
    ['/campaigns', 'Campanhas'],
    ['/tasks', 'Tarefas'],
    ['/indication-program', 'Programa de indicação'],
    ['/indicators', 'Indicadores'],
  ]),
  ...routeGroup('Configurações', [
    ['/settings', 'Configurações'],
    ['/settings/:key', 'Editar configuração'],
    ['/settings/absence-messages', 'Mensagens de ausência'],
    ['/settings/anniversary-messages', 'Mensagens de aniversário'],
    ['/settings/anniversary-messages/:key', 'Editar mensagem de aniversário'],
    ['/settings/department-routing', 'Bot de atendimento inicial'],
    ['/settings/conversation-finish', 'Finalização de atendimento'],
    ['/settings/quick-messages', 'Mensagens rápidas'],
    ['/settings/whatsapp-routing', 'Uso da API oficial'],
    ['/settings/whatsapp-templates', 'Templates WhatsApp'],
    ['/settings/whatsapp-templates/new', 'Criar template WhatsApp'],
    ['/settings/whatsapp-templates/:id', 'Editar template WhatsApp'],
  ]),
  ...routeGroup('Demandas', [
    ['/settings/demands', 'Demandas'],
    ['/settings/demands/new', 'Criar demanda'],
    ['/settings/demands/:id', 'Editar demanda'],
    ['/settings/demands/view/:id', 'Visualizar demanda'],
    ['/settings/demands/import', 'Importar demandas'],
  ]),
  ...routeGroup('Cadastros', [
    ['/settings/departments', 'Departamentos'],
    ['/settings/departments/new', 'Criar departamento'],
    ['/settings/departments/:id', 'Editar departamento'],
    ['/settings/niches', 'Segmentos'],
    ['/settings/niches/new', 'Criar segmento'],
    ['/settings/niches/:id', 'Editar segmento'],
    ['/settings/tags', 'Tags'],
    ['/settings/tags/new', 'Criar tag'],
    ['/settings/tags/:id', 'Editar tag'],
    ['/settings/client-origins', 'Origens dos clientes'],
    ['/settings/client-origins/new', 'Criar origem de cliente'],
    ['/settings/client-origins/:id', 'Editar origem de cliente'],
    ['/settings/cac-expenses', 'Despesas de CAC'],
    ['/settings/cac-expenses/new', 'Criar despesa de CAC'],
    ['/settings/cac-expenses/:id', 'Editar despesa de CAC'],
  ]),
  ...routeGroup('Administração', [
    ['/companies', 'Empresas'],
    ['/companies/new', 'Criar empresa'],
    ['/companies/:id', 'Editar empresa'],
    ['/users', 'Usuários'],
    ['/users/new', 'Criar usuário'],
    ['/users/:id', 'Editar usuário'],
    ['/admin-indicators', 'Indicadores administrativos'],
    ['/settings/journey-templates', 'Jornadas padrão'],
    ['/settings/journey-templates/new', 'Criar jornada padrão'],
    ['/settings/journey-templates/:id', 'Editar jornada padrão'],
    ['/settings/plans', 'Planos'],
    ['/settings/plans/new', 'Criar plano'],
    ['/settings/plans/:id', 'Editar plano'],
    ['/settings/notifications', 'Avisos'],
    ['/settings/notifications/new', 'Criar aviso'],
    ['/settings/notifications/:id', 'Editar aviso'],
    ['/settings/system-update', 'Atualização do sistema'],
    ['/settings/tutorials', 'Tutoriais'],
    ['/settings/tutorials/new', 'Criar tutorial'],
    ['/settings/tutorials/:id', 'Editar tutorial'],
    ['/settings/help-center', 'Central de Ajuda'],
    ['/settings/help-center/new', 'Criar material de ajuda'],
    ['/settings/help-center/:id', 'Editar material de ajuda'],
    ['/billing/update', 'Atualizar assinatura'],
  ]),
].sort((left, right) =>
  left.module.localeCompare(right.module, 'pt-BR', { sensitivity: 'base' })
)

export function tutorialRouteMatchesPath(route: string, pathname: string) {
  const routeParts = route.split(UTILS.SLASH).filter(Boolean)
  const pathnameParts = pathname.split(UTILS.SLASH).filter(Boolean)

  return (
    routeParts.length === pathnameParts.length &&
    routeParts.every(
      (part, index) => part.startsWith(':') || part === pathnameParts[index]
    )
  )
}
